import { useState } from 'react'

import {
  PAGE_SIZE,
  useGetLicenseRequestSubmissionsQuery,
} from '@disco/state-management/api/license-requests'

import type { ClientLibraryId } from 'types/client-library'

import { toApiDateString } from './format-date'

export type { LicenseRequestSubmission } from '@disco/state-management/api/license-requests'

export type DateRange = { from: Date; to: Date }

export function useLicenseRequestSubmissions(
  libraryId: ClientLibraryId,
  ordering: string,
  dateRange: DateRange | null = null,
) {
  const [page, setPage] = useState(1)
  const [prevOrdering, setPrevOrdering] = useState(ordering)
  const [prevDateRange, setPrevDateRange] = useState(dateRange)

  if (prevOrdering !== ordering || prevDateRange !== dateRange) {
    setPrevOrdering(ordering)
    setPrevDateRange(dateRange)
    setPage(1)
  }

  const fromDate = dateRange ? toApiDateString(dateRange.from) : undefined
  const toDate = dateRange ? toApiDateString(dateRange.to) : undefined

  const { data, isLoading, isFetching, error } =
    useGetLicenseRequestSubmissionsQuery({
      libraryId,
      ordering,
      page,
      fromDate,
      toDate,
    })

  const total = data?.count ?? 0
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const end = Math.min(page * PAGE_SIZE, total)
  const hasNext = Boolean(data?.next)
  const hasPrev = page > 1

  const goNext = () => {
    if (hasNext) {
      setPage(p => p + 1)
    }
  }

  const goPrev = () => {
    if (hasPrev) {
      setPage(p => p - 1)
    }
  }

  return {
    submissions: data?.results ?? [],
    total,
    page,
    start,
    end,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    isLoading: isLoading || isFetching,
    error,
  }
}
